import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { ProductPicker, SizeGrid } from "@/components/NewBatchDialog";
import { useSortedProducts } from "@/components/cutting/AddQueueDialog";

const NO_MAT = "nessuno";

const ItemBlock = ({ item, idx, onChange, onRemove }) => (
  <div className="space-y-2 rounded-md border border-border/60 p-3" data-testid={`cut-item-${idx}`}>
    <div className="flex items-center justify-between gap-2">
      <p className="truncate text-sm font-medium">{item.product.name} <span className="text-muted-foreground">· {item.product.color}</span>{item.queue_id && <span className="ml-1 text-xs text-primary">(dalla coda)</span>}</p>
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onRemove} data-testid={`cut-item-remove-${idx}`}><X className="h-4 w-4" /></Button>
    </div>
    <SizeGrid sizes={(item.product.sizes || []).map((s) => s.size)} values={item.qty} onChange={(qty) => onChange({ ...item, qty })} testid={`cut-item-${idx}-size`} />
  </div>
);

export const CutLogDialog = ({ initial, onClose }) => {
  const qc = useQueryClient();
  const [items, setItems] = useState(initial.items);
  const [adding, setAdding] = useState(initial.items.length === 0);
  const [f, setF] = useState({ date: new Date().toLocaleDateString("sv-SE"), markers: "1", fabric_meters: "", material_id: NO_MAT, cutter: "", sample: false, notes: "" });
  const products = useSortedProducts(adding);
  const { data: cutters = [] } = useQuery({ queryKey: ["cutting", "cutters"], queryFn: async () => (await api.get("/cutting/cutters")).data });
  const { data: materials = [] } = useQuery({ queryKey: ["materials"], queryFn: async () => (await api.get("/materials")).data });
  const fabrics = materials.filter((m) => m.type === "tessuto");
  const total = items.reduce((s, it) => s + Object.values(it.qty).reduce((a, v) => a + (Number(v) || 0), 0), 0);
  const save = useMutation({
    mutationFn: async () => (await api.post("/cutting/logs", {
      ...f, markers: Number(f.markers) || 0, fabric_meters: Number(f.fabric_meters) || 0, material_id: f.material_id === NO_MAT ? null : f.material_id, notes: f.notes || null,
      items: items.map((it) => ({ product_id: it.product.id, queue_id: it.queue_id || null, sizes: Object.entries(it.qty).map(([size, v]) => ({ size, quantity: Number(v) || 0 })) })),
    })).data,
    onSuccess: () => { ["cutting", "materials"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); toast.success(f.sample ? "Campione registrato" : "Taglio registrato"); onClose(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const submit = () => {
    if (!total) { toast.error("Inserisci i pezzi tagliati"); return; }
    if (!f.cutter.trim()) { toast.error("Indica chi ha tagliato"); return; }
    save.mutate();
  };
  const inp = (k, type = "text", extra = {}) => <Input type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`cut-log-${k}`} {...extra} />;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle>Registra taglio</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between rounded-md border border-border/60 p-3">
            <div><p className="text-sm font-medium">Campione</p><p className="text-xs text-muted-foreground">Non conta nella produzione, solo nelle statistiche dei campioni.</p></div>
            <Switch checked={f.sample} onCheckedChange={(v) => setF({ ...f, sample: v })} data-testid="cut-log-sample" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between"><Label>Articoli del grafico</Label><span className="text-xs text-muted-foreground" data-testid="cut-log-total">Totale: {num(total)} pz</span></div>
            {items.map((it, i) => <ItemBlock key={`${it.product.id}-${it.queue_id || i}`} item={it} idx={i} onChange={(v) => setItems(items.map((x, j) => (j === i ? v : x)))} onRemove={() => setItems(items.filter((_, j) => j !== i))} />)}
            {adding ? <ProductPicker products={products} selected={null} onSelect={(p) => { if (p) { setItems([...items, { product: p, qty: {} }]); setAdding(false); } }} />
              : <Button variant="outline" size="sm" onClick={() => setAdding(true)} data-testid="cut-log-add-item"><Plus className="mr-1 h-4 w-4" />Aggiungi articolo allo stesso grafico</Button>}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="space-y-1.5"><Label>Data</Label>{inp("date", "date")}</div>
            <div className="space-y-1.5"><Label>Grafici tagliati</Label>{inp("markers", "number", { min: 0 })}</div>
            <div className="space-y-1.5"><Label>Metri di tessuto</Label>{inp("fabric_meters", "number", { min: 0, step: "0.1" })}</div>
            <div className="space-y-1.5"><Label>Chi ha tagliato</Label>{inp("cutter", "text", { list: "cutters-list" })}<datalist id="cutters-list">{cutters.map((c) => <option key={c} value={c} />)}</datalist></div>
            <div className="col-span-2 space-y-1.5">
              <Label>Tessuto da scalare (facoltativo)</Label>
              <Select value={f.material_id} onValueChange={(v) => setF({ ...f, material_id: v })}>
                <SelectTrigger data-testid="cut-log-material"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_MAT}>Non scalare dal magazzino</SelectItem>
                  {fabrics.map((m) => <SelectItem key={m.id} value={m.id}>{[m.name, m.color, m.supplier].filter(Boolean).join(" · ")} ({num(m.quantity)} m)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5 sm:col-span-3"><Label>Note</Label>{inp("notes")}</div>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={save.isPending} data-testid="cut-log-save-btn">{save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
